import { useQuery } from "@apollo/client";
import Button from "components/base/Button";
import Errors from "components/base/Errors";
import Page from "components/base/Page";
import SearchBar from "components/base/SearchBar";
import Spinner from "components/base/Spinner";
import ProductSelector from "components/shared/ProductSelector";
import { useModals } from "ModalProvider";
import { GoHistory } from "react-icons/go";
import { Link, useParams, useSearchParams } from "react-router-dom";
import exportWarehouseStock from "../../../amazon/stocks/exportXlxs";
import InboundFormView from "../../../amazon/stocks/InboundFormView";
import { FETCH_PRODUCTS_FOR_WAREHOUSE, FETCH_STOCK_CATEGORY } from "../graphql";
import StockDetailView from "./StockDetailView";
import StockForm from "./StockForm";

const WarehousePage = () => {
  const { stockCategoryId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams({ q: "" });
  const q = searchParams.get("q") || "";
  const normalizedQ = q.trim().toLowerCase();
  const { loading, error, data } = useQuery(FETCH_STOCK_CATEGORY, {
    variables: { stockCategoryId },
    fetchPolicy: "network-only",
  });
  const chooseProductModal = useModals();
  const stockModal = useModals();
  const stockDetailModal = useModals();
  const inboundModal = useModals();

  const category = data?.stockCategory;
  const isAlcCategory = category?.name.includes("ALC");
  const stocks = category?.stocks || [];
  const latestStocks = data?.stockCategoryLatestStocks
    ? JSON.parse(data.stockCategoryLatestStocks)
    : {};

  if (loading) return <Spinner />;
  if (error) return <Errors error={error} />;

  // Merge latest stock quantities into stocks
  const stocksWithLatestQty = stocks
    .map((stock) => ({
      ...stock,
      latestQty: latestStocks[stock.id]?.latest_qty ?? 0,
    }))
    .sort((a, b) => a.product?.number.localeCompare(b.product?.number));

  const inbounds = (category?.inbounds || []).map((inbound) => {
    const lines = inbound.lines.map((line) => {
      const reserves = (category?.stockReserves || []).filter(
        (item) => item.reserveId === parseInt(line.id, 10),
      );
      const reservedQty = reserves.reduce(
        (acc, reserve) => acc + reserve.qty,
        0,
      );
      return { ...line, reserves, reservedQty };
    });
    return { ...inbound, lines };
  });

  const sortedInbounds = [...inbounds].sort(
    (a, b) => new Date(a.eta) - new Date(b.eta),
  );

  const inboundCategory = category
    ? {
        ...category,
        stocks: stocks.map((stock) => ({
          ...stock,
          name: stock.name || stock.product?.name || "",
          number: stock.number || stock.product?.number || "",
          odooId: stock.odooId || stock.product?.odooId,
        })),
      }
    : null;

  const filteredStocks = stocksWithLatestQty.filter((stock) => {
    if (!normalizedQ) return true;
    const number = (stock.product?.number || "").toLowerCase();
    const name = (stock.product?.name || "").toLowerCase();
    return number.includes(normalizedQ) || name.includes(normalizedQ);
  });

  const exportStocks = stocksWithLatestQty.map((stock) => ({
    ...stock,
    name: stock.name || stock.product?.name || "",
    number: stock.number || stock.product?.number || "",
  }));

  function addProduct() {
    chooseProductModal.present({
      title: "Choose Product",
      children: (
        <ProductSelector
          query={FETCH_PRODUCTS_FOR_WAREHOUSE}
          onSelect={didChooseProduct}
        />
      ),
    });
  }

  function didChooseProduct(product) {
    chooseProductModal.hide();
    stockModal.present({
      title: "Add Product Stock",
      center: true,
      children: (
        <StockForm
          categoryId={stockCategoryId}
          categoryName={category?.name || ""}
          product={product}
          mode="create"
          initialValues={{
            name: product?.name || "",
            number: product?.number || "",
            asin: "",
            barcode: "",
            unitCost: "",
            initialQty: 0,
          }}
          hide={stockModal.hide}
        />
      ),
    });
  }

  function editStock(stock) {
    stockModal.present({
      title: "Edit Product Stock",
      center: true,
      children: (
        <StockForm
          categoryId={stockCategoryId}
          categoryName={category?.name || ""}
          product={stock.product}
          mode="edit"
          initialValues={{
            id: stock.id,
            name: stock.name || stock.product?.name || "",
            number: stock.number || stock.product?.number || "",
            asin: stock.asin || "",
            barcode: stock.barcode || "",
            unitCost: stock.unitCost ?? "",
            initialQty: stock.initialQty,
          }}
          hide={stockModal.hide}
        />
      ),
    });
  }

  function showStockDetails(stock) {
    stockDetailModal.present({
      title: stock.product.number,
      subtitle: "Stock Records",
      maxWidth: "max-w-7xl",
      children: (
        <StockDetailView
          stock={stock}
          stockCategoryId={stockCategoryId}
          latestQty={latestStocks[stock.id]?.latest_qty || 0}
        />
      ),
    });
  }

  function tryCreateInbound() {
    inboundModal.present({
      title: "Create Inbound",
      children: (
        <InboundFormView category={inboundCategory} hide={inboundModal.hide} />
      ),
      isBeingPresented: true,
    });
  }

  function showInboundDetail(inbound) {
    inboundModal.present({
      title: inbound.name,
      children: (
        <InboundFormView
          category={inboundCategory}
          originInbound={inbound}
          hide={inboundModal.hide}
        />
      ),
    });
  }

  return (
    <Page
      title={category?.name || "Warehouse"}
      backTo="./.."
      className="h-screen"
      rightButtons={
        <div className="flex items-center space-x-4">
          <Link to="shipments/new">+ New Stock Record</Link>
          <Link to="shipments">Stock Records</Link>

          {isAlcCategory && <Link to="compare">Compare with ALC</Link>}
          <a
            href={`${process.env.REACT_APP_SERVER_ADMIN_URL}amazon_vendor_center/stockcategory/158/change/`}
            target="_blank"
            rel="noreferrer"
          >
            Admin
          </a>
        </div>
      }
    >
      <div className="p-6 h-full flex flex-col">
        <div className="mb-4 flex items-center space-x-4">
          <div className="flex-1">
            <SearchBar
              initialQuery={q}
              placeholder="Search by product number or name"
              onChange={(nextQ) => setSearchParams({ q: nextQ })}
            />
          </div>

          <Button onClick={addProduct}>+ Add Product</Button>
          <Button onClick={tryCreateInbound}>+ Inbound Col</Button>
          <Button
            onClick={() =>
              exportWarehouseStock(exportStocks, sortedInbounds, [])
            }
          >
            Export XLXS
          </Button>
        </div>

        <div className="card p-0 flex-1 overflow-auto flex flex-col">
          <div className="flex-1 overflow-auto relative">
            {filteredStocks.length === 0 ? (
              <div className="p-8 text-sm text-gray-500">
                No products found in this warehouse.
              </div>
            ) : (
              <div className="whitespace-nowrap">
                <table>
                  <thead>
                    <tr className="sticky top-0 z-10 backdrop-blur bg-gray-50 bg-opacity-80 dark:bg-gray-700/50">
                      <th className="px-4 py-3">
                        Product({filteredStocks.length})
                      </th>
                      <th className="px-4 text-right">Latest Qty</th>
                      <th className="px-4">ASIN</th>
                      <th className="px-4">Barcode</th>
                      <th className="px-4 text-right">Qty / Master</th>
                      {sortedInbounds.map((inbound) => (
                        <th key={inbound.id} className="px-4 text-right">
                          <div className="flex flex-col items-end">
                            <Button onClick={() => showInboundDetail(inbound)}>
                              {inbound.name}
                            </Button>
                            <div className="text-xs font-normal opacity-70">
                              {inbound.eta}
                            </div>
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredStocks.map((stock) => (
                      <tr
                        key={stock.id}
                        className="border-y border-gray-100 dark:border-gray-700 hover:bg-sky-50 dark:hover:bg-sky-900/20"
                      >
                        <td className="px-4">
                          <Button onClick={() => editStock(stock)}>
                            [{stock.product?.number}]{" "}
                            {stock.name || stock.product?.name} ✍🏻
                          </Button>
                        </td>
                        <td className="px-4 text-right">
                          <div className="flex justify-end">
                            <Button
                              onClick={() => showStockDetails(stock)}
                              danger={stock.latestQty < 0}
                              icon={<GoHistory />}
                            >
                              {stock.latestQty}
                            </Button>
                          </div>
                        </td>
                        <td className="px-4">{stock.asin}</td>
                        <td className="px-4">{stock.barcode}</td>
                        <td className="px-4 text-right">
                          {stock.product.itemsPerSet}
                        </td>
                        {sortedInbounds.map((inbound) => {
                          const inboundLine = inbound.lines.find(
                            (line) => line.stock.id === stock.id,
                          );
                          const qty = inboundLine ? inboundLine.qty : 0;
                          const reservedQty = inboundLine
                            ? inboundLine.reservedQty
                            : 0;
                          return (
                            <td key={inbound.id} className="px-4 text-right">
                              <span>{qty}</span>
                              {reservedQty > 0 && (
                                <span
                                  data-tips={inboundLine.reserves
                                    .map(
                                      (reserve) =>
                                        `${reserve.shipmentLine.shipment.refName} ⨉ ${reserve.qty}`,
                                    )
                                    .join("\n")}
                                >
                                  {" "}
                                  (R: {reservedQty}) → {qty - reservedQty}
                                </span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </Page>
  );
};

export default WarehousePage;
